<template>
  <v-container fluid class="fill-height reset-container">
    <v-row justify="center" align="center" class="fill-height">
      <v-col cols="12" sm="8" md="5" lg="4">
        <v-card class="reset-shell pa-8">
          <div class="text-center mb-6">
            <v-avatar size="72" color="white" class="elevation-3 mb-4">
              <!-- <img>, e não <v-img>: o webpack só resolve "@/assets/..." em <img>.
                 No <v-img> o caminho ia cru e o avatar ficava um círculo vazio. -->
                      <img src="@/assets/salvia_symbol.svg" alt="Salvia" width="44" height="44" />
            </v-avatar>
            <h2 class="reset-title mb-2">Criar nova senha</h2>
            <p class="reset-subtitle mb-0">
              {{ email || "Defina a senha de acesso da sua conta" }}
            </p>
          </div>

          <!-- Link inválido: nem adianta mostrar o formulário -->
          <div v-if="!hasValidLink" class="text-center py-4">
            <v-icon size="52" color="warning" class="mb-3">
              mdi-link-variant-off
            </v-icon>
            <p class="reset-subtitle mb-6">
              Este link de redefinição está incompleto ou inválido. Solicite um
              novo na tela de login.
            </p>
            <v-btn color="primary" rounded block height="46" @click="goToLogin">
              Voltar para o login
            </v-btn>
          </div>

          <!-- Sucesso -->
          <div v-else-if="done" class="text-center py-4">
            <v-icon size="52" color="primary" class="mb-3">
              mdi-check-circle-outline
            </v-icon>
            <p class="reset-subtitle mb-6">
              Senha redefinida com sucesso. Suas sessões antigas foram
              encerradas — entre novamente com a nova senha.
            </p>
            <v-btn color="primary" rounded block height="46" @click="goToLogin">
              Ir para o login
            </v-btn>
          </div>

          <!-- Formulário -->
          <v-form
            v-else
            ref="form"
            v-model="validForm"
            @submit.prevent="submit"
          >
            <v-text-field
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              label="Nova senha (mín. 10 caracteres)"
              prepend-inner-icon="mdi-lock-outline"
              outlined
              color="primary"
              class="mb-2"
              :rules="[rules.required, rules.minLength]"
              :disabled="loading"
            >
              <template v-slot:append>
                <v-icon @click="showPassword = !showPassword">
                  {{ showPassword ? "mdi-eye" : "mdi-eye-off" }}
                </v-icon>
              </template>
            </v-text-field>

            <PasswordRequirements :senha="password" />

            <v-text-field
              v-model="passwordConfirmation"
              :type="showPassword ? 'text' : 'password'"
              label="Confirmar nova senha"
              prepend-inner-icon="mdi-lock-check-outline"
              outlined
              color="primary"
              class="mb-2"
              :rules="[rules.required, rules.matches]"
              :disabled="loading"
              @keydown.enter="submit"
            />

            <div v-if="errorMessage" class="auth-error-msg mb-4">
              <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
              {{ errorMessage }}
            </div>

            <v-btn
              type="submit"
              color="primary"
              rounded
              block
              height="48"
              class="reset-submit"
              :loading="loading"
              :disabled="!validForm"
            >
              Redefinir senha
            </v-btn>

            <v-btn text block small class="mt-3" @click="goToLogin">
              Cancelar e voltar ao login
            </v-btn>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PasswordRequirements from "@/components/shared/PasswordRequirements.vue";
import { regraDeSenhaForte } from "@/utils/politicaDeSenha";
import { resetPassword, extractPasswordError } from "@/services/passwordService";

export default {
  name: "ResetPasswordPage",
  components: { PasswordRequirements },

  data() {
    return {
      password: "",
      passwordConfirmation: "",
      showPassword: false,
      validForm: false,
      loading: false,
      done: false,
      errorMessage: "",
      rules: {
        required: (v) => !!v || "Este campo é obrigatório",
        // A política completa do backend (ver utils/politicaDeSenha).
        minLength: (v) => regraDeSenhaForte(v),
        matches: (v) => v === this.password || "As senhas não conferem",
      },
    };
  },

  computed: {
    // Token e e-mail chegam na query do link enviado por e-mail.
    token() {
      return this.$route.query.token || "";
    },
    email() {
      return this.$route.query.email || "";
    },
    hasValidLink() {
      return !!this.token && !!this.email;
    },
  },

  methods: {
    goToLogin() {
      this.$router.push({ name: "LoginPage" });
    },

    async submit() {
      if (!this.$refs.form?.validate()) return;

      this.loading = true;
      this.errorMessage = "";
      try {
        await resetPassword({
          token: this.token,
          email: this.email,
          password: this.password,
          passwordConfirmation: this.passwordConfirmation,
        });
        this.done = true;
      } catch (err) {
        this.errorMessage = extractPasswordError(
          err,
          "Não foi possível redefinir a senha. O link pode ter expirado.",
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.reset-container {
  background: radial-gradient(
    1200px circle at 20% 10%,
    var(--ds-blue-100) 0%,
    var(--ds-surface-white) 45%,
    var(--ds-blue-bg) 100%
  );
}

.reset-shell {
  border-radius: 18px !important;
  box-shadow: 0 16px 40px rgba(31, 77, 63, 0.1),
    0 2px 8px rgba(0, 0, 0, 0.04) !important;
}

.reset-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--ds-text-strong);
  letter-spacing: -0.5px;
}

.reset-subtitle {
  font-size: 13.5px;
  color: var(--ds-text-soft);
  font-weight: 500;
  line-height: 1.6;
}

.auth-error-msg {
  display: flex;
  align-items: center;
  font-size: 12.5px;
  color: var(--ds-error, #d32f2f);
}

.reset-submit {
  font-weight: 700 !important;
  letter-spacing: 0.3px !important;
  text-transform: none !important;
}
</style>
